import { Col, Container, Row, Spinner } from "react-bootstrap";
import { IMPORT_MODE, importModeLabels } from "@/constant/Imports/importMode";
import React, { useMemo, useState } from "react";
import {
  parseImportFile,
  validateMappingImportData,
} from "@/utils/ImportUtils";

import BasicImportInitialValues from "@/data/basic-import-initial-values";
import { BasicImportSchema } from "@/schema/BasicImportSchema";
import Button from "@/components/Button";
import Input from "@/components/Input/Input";
import ModalSkeleton from "@/components/ModalSkeleton";
import RenderToast from "@/components/RenderToast";
import ScrollContainer from "react-indiana-drag-scroll";
import Table from "@/components/Table";
import classes from "./styles";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { usePost } from "@/apiHooks/HttpFunctions";
import { useSearchParams } from "react-router-dom";
import validateMissingCodes from "./validateMissingCodes";

export default function BaseImport({
  setShow,
  show,
  api,
  columns,
  nullableColumns = [],
  shouldUniqueColumn = "",
  shouldExistColumns = {},
  shouldBeUniqueCompositeColumns = [],
  dataName = "",
  enableAddMode = true,
}) {
  const [failedRowsTableData, setFailedRowsTableData] = useState([]);
  const [, setSearchParams] = useSearchParams();

  const showError = (errorList, toastMessage = "Request failed.") => {
    setFailedRowsTableData(errorList);
    RenderToast({
      type: "error",
      message: toastMessage,
    });
  };

  const {
    mutate: save,
    error: saveError,
    isPending: saveIsPending,
  } = usePost({
    apiURL: `${api}/import`,
    isFormData: true,
    onSuccess: (data) => {
      closeModal();
      RenderToast({
        type: "success",
        message: `${dataName} imported successfully.`,
      });
      setSearchParams({ page: "1" });
    },
    onError: (error) => {
      const errorData = error.response?.data?.errors || [];
      const errorList = errorData?.map((error) => ({
        row: error.row,
        error: error.error,
      }));
      showError(errorList, "Import failed.");
    },
  });

  const { mutate: validateCodes, isPending: validateCodesIsPending } = usePost({
    apiURL: `${api}/import/validate-codes`,
    onError: (error) => {
      const errorData = error.response?.data?.errors || [];
      const errorList = errorData.map((error) => ({
        row: error.row,
        error: error.error,
      }));
      showError(errorList, "Table ID validation failed.");
    },
  });

  const importModeLabelsMap = importModeLabels();
  const modeRadioOptions = [
    enableAddMode && {
      value: IMPORT_MODE.ADD,
      label: importModeLabelsMap[IMPORT_MODE.ADD],
    },
    {
      value: IMPORT_MODE.ADD_OVERWRITE,
      label: importModeLabelsMap[IMPORT_MODE.ADD_OVERWRITE],
    },
    {
      value: IMPORT_MODE.ADD_ERASE_ALL_EXISTING,
      label: importModeLabelsMap[IMPORT_MODE.ADD_ERASE_ALL_EXISTING],
    },
  ].filter(Boolean);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: BasicImportInitialValues(
      enableAddMode ? IMPORT_MODE.ADD : IMPORT_MODE.ADD_OVERWRITE
    ),
    validationSchema: BasicImportSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const tableHeadings = useMemo(
    () => [
      {
        id: "row",
        label: "Row Number",
        style: { borderTop: 0, width: "100%" },
      },
      {
        id: "error",
        label: "Error",
        style: { borderTop: 0, width: "100%" },
        bodyStyle: { overflow: "visible", display: "block" },
      },
    ],
    []
  );

  const [validateMissingCodesIsPending, setValidateMissingCodesIsPending] =
    useState(false);
  const handleSubmit = async (values) => {
    setFailedRowsTableData([]);

    const file = values.data_file;
    const parsedData = await parseImportFile(file);
    let errors;

    errors = validateMappingImportData(
      parsedData,
      columns,
      nullableColumns,
      shouldUniqueColumn,
      shouldBeUniqueCompositeColumns
    );
    if (errors.length > 0) {
      showError(errors, "Validation error.");
      return;
    }

    setValidateMissingCodesIsPending(true);
    errors = await validateMissingCodes(
      shouldExistColumns,
      nullableColumns,
      parsedData
    );
    setValidateMissingCodesIsPending(false);
    if (errors.length > 0) {
      showError(errors, "Validation error.");
      return;
    }

    proceedToImport(values, parsedData);
  };
  const proceedToImport = (values, parsedData) => {
    const formData = new FormData();
    formData.append("data_file", values.data_file);
    formData.append("mode", values.mode);

    if (values.mode === IMPORT_MODE.ADD) {
      const normalizedShouldUniqueColumn = shouldUniqueColumn.toLowerCase();

      const codes = parsedData.map((row, index) => {
        return {
          row: index + 2,
          code: row[normalizedShouldUniqueColumn],
        };
      });

      validateCodes(
        { codes },
        {
          onSuccess: () => {
            save(formData);
          },
        }
      );
    } else {
      save(formData);
    }
  };

  const closeModal = () => {
    formik.resetForm();
    setFailedRowsTableData([]);
    setShow(false);
  };

  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <div>
      <ModalSkeleton
        header={`Import ${dataName}`}
        onHide={closeModal}
        show={show}
        modalStyles={classes.modal}
        showCloseIcon={true}
      >
        <form onSubmit={formik.handleSubmit} className={classes.formDiv}>
          <Row>
            <Col md={12}>
              <Input
                type="file"
                labelClass={classes.label}
                label="Import File"
                className={classes.inputDiv}
                inputContainerClass={classes.input}
                value={formik.values.data_file}
                setValue={(val) => formik.setFieldValue("data_file", val)}
                errorTextClass={classes.errorText}
                errorText={
                  (formik.touched.data_file && formik.errors.data_file) ||
                  (saveError?.status === 422 &&
                    saveError?.response?.data?.errors?.data_file?.join(" "))
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={4}></Col>
            <Col md={8}>
              <div className="ml-10">
                {modeRadioOptions.map((option) => (
                  <Row key={option.value}>
                    <Col md={12}>
                      <label className={classes.radioButton}>
                        <input
                          id={`mode-${option.value}`}
                          type="radio"
                          value={option.value}
                          checked={formik.values.mode === option.value}
                          onChange={() =>
                            formik.setFieldValue("mode", option.value)
                          }
                        />
                        <span>{option.label}</span>
                      </label>
                    </Col>
                  </Row>
                ))}
              </div>
              {((formik.touched.mode && formik.errors.mode) ||
                saveError?.status === 422) && (
                <div className={classes.error}>
                  {formik?.errors?.mode ??
                    saveError?.response?.data?.errors?.mode?.join(" ")}
                </div>
              )}
            </Col>
          </Row>
          <div className="mt-4 gap-x-10 flex justify-center">
            <div className="w-1/3">
              <Button
                variant="outlined"
                label="Cancel"
                onClick={closeModal}
                type="button"
                className={classes.button}
                disabled={
                  saveIsPending ||
                  validateCodesIsPending ||
                  validateMissingCodesIsPending
                }
              />
            </div>
            <div className="w-1/3">
              <Button
                variant="green-gradient"
                label={
                  saveIsPending ||
                  validateCodesIsPending ||
                  validateMissingCodesIsPending ? (
                    <Spinner size="sm" />
                  ) : (
                    "Upload"
                  )
                }
                type="submit"
                className={classes.button}
                disabled={
                  saveIsPending ||
                  validateCodesIsPending ||
                  validateMissingCodesIsPending
                }
              />
            </div>
          </div>
        </form>

        {failedRowsTableData.length > 0 && (
          <Container fluid className="py-4">
            {/* Failed Rows Section */}
            <h2 className="mb-3">Failed Rows</h2>
            <Row>
              <Col md={12}>
                <div className={classes.table}>
                  <ScrollContainer>
                    <Table
                      loading={
                        saveIsPending ||
                        validateCodesIsPending ||
                        validateMissingCodesIsPending
                      }
                      tableData={failedRowsTableData}
                      headings={tableHeadings}
                      striped
                      hover
                    />
                  </ScrollContainer>
                </div>
              </Col>
            </Row>
          </Container>
        )}
      </ModalSkeleton>
    </div>
  );
}
